import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  availableKeywordsUse,
  availableSearchUse,
  GetMoreSearches,
  paths,
  searchesProgressBarTexts,
  userRoles
} from 'src/common/config/TextMessage';
import type { RootState } from 'src/redux/store';
import {
  keywordResearchDashBoardCss,
  searchProgressSx
} from './keywordResearchCSS';
import { Alert, Box, Snackbar, Typography } from '@mui/material';
import { CustomTooltip } from './CustomTooltip';
import { snackBar } from './keywordOverview/keywordOverviewCSS';
import HelpIcon from '@mui/icons-material/Help';
import CommonModal from 'src/common/config/CommonModal';
import { BuySearchCreditModal } from './BuySearchCreditModal';
import {
  setOpenSearchCreditModal,
  setSelectedSearches
} from 'src/redux/keywordResearch';
import { setIsOpenChangePlan } from 'src/redux/siteAuditReducer';
import { GenerateIdeasLoader } from './blogPostIdeas/GenerateIdeasLoader';
import { getCreditDetails } from 'src/common/Scraper';
import { updateSearchesDetailsAndLimits } from 'src/common/config/FunctionHelperPartTwo';
/**
 * ScanProgress component displays the progress of scans for the user.
 *
 * @returns {JSX.Element} Scan progress information.
 */
const SearchProgress = ({
  more = true,
  isKeywordTracking = false,
  isMapRank = false
}) => {
  const [scanDetails, setScanDetails] = useState(0);
  const [open, setOpen] = useState(null);
  const [openCreditModal, setOpenCreditModal] = useState(null);
  const [openCreditProcessModal, setOpenCreditProcessModal] = useState(false);
  const [isError, setIsError] = useState(null);
  let searchesDetailRedux: any = useSelector(
    (state: RootState) => state.keywordResearch.searchesDetails
  );
  let selectedSearches = useSelector(
    (state: RootState) => state.keywordResearch.selectedSearches
  );
  let openSearchCreditModal = useSelector(
    (state: RootState) => state.keywordResearch.openCreditModal
  );
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  const isDIY = activePlanName === planDetails.diy || activePlanName === planDetails.starterPlus;
  const totalAddonLimit = searchesDetailRedux?.['totalAddonLimit'];
  const totalAddonUsedLimit = searchesDetailRedux?.['totalAddonUsedLimit'];
  const keywordUsed = searchesDetailRedux?.keywordUsed || 0;
  const keywordLimit = searchesDetailRedux?.keywordLimit || 0;
  const finalizedUsedLimit =
    searchesDetailRedux?.['usedLimit'] + totalAddonUsedLimit;
  const finalizedAddonUsedLimit =
    searchesDetailRedux?.['totalLimit'] + totalAddonLimit;
  const isEnterprise = activePlanName === planDetails?.enterprise;
  const mapRankUsed = searchesDetailRedux?.mapRankUsed || 0;
  const mapRankTotal = searchesDetailRedux?.mapRankLimit || 0;

  const isPremium = useSelector((state: RootState) => state.user.premiumUser);
  const userDetails: any =
    useSelector((state: RootState) => state.counter.userDetails) ?? {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getCredits = async () => {
    let getSearchesDetail;
    if (
      searchesDetailRedux &&
      ((Array.isArray(searchesDetailRedux) && searchesDetailRedux.length > 0) ||
        (!Array.isArray(searchesDetailRedux) &&
          Object.keys(searchesDetailRedux).length > 0))
    ) {
      getSearchesDetail = searchesDetailRedux;
    } else {
      getSearchesDetail = await getCreditDetails();
    }
    // let getSearchesDetail: any = Array.isArray(searchesDetailRedux) && searchesDetailRedux.length > 0 ? searchesDetailRedux : await getCreditDetails();
    if (getSearchesDetail) {
      updateSearchesDetailsAndLimits(getSearchesDetail);
    }
  };

  useEffect(() => {
    if (isPremium) {
      if (isMapRank) {
        setScanDetails(Number(mapRankUsed / mapRankTotal) * 100);
      } else if (isKeywordTracking) {
        setScanDetails(Number(keywordUsed / keywordLimit) * 100);
      } else {
        setScanDetails(
          Number(finalizedUsedLimit / finalizedAddonUsedLimit) * 100
        );
      }
    }

  }, [searchesDetailRedux]);

  useEffect(() => {
    if (openSearchCreditModal) {
      setOpenCreditModal(true);
      dispatch(setOpenSearchCreditModal(null));
    }
  }, [openSearchCreditModal]);

  useEffect(() => {
    if (selectedSearches) {
      setOpen(true);
      setTimeout(() => {
        setOpen(null);
        dispatch(setSelectedSearches(null));
      }, 5000);
    }
  }, [selectedSearches]);

  useEffect(() => {
    if (isError) {
      setOpen(true);
      setTimeout(() => {
        setOpen(null);
        setIsError(null);
      }, 5000);
    }
  }, [isError]);

  useEffect(() => {
    getCredits();
  }, []);

  const handleCloseCreditModal = () => {
    setOpenCreditModal(false);
  };

  const handleCloseProcessCreditModal = () => {
    setOpenCreditProcessModal(false);
  };

  const getSearchProgressColor = (progress = 0) => {
    const getColorByUsage = (used: number, total: number) => {
      if (total === 0) return 'rgba(50, 213, 131, 0.8)'; // Avoid division by zero

      const percentageUsed = (used / total) * 100;

      if (percentageUsed < 60) {
        return 'rgba(50, 213, 131, 0.8)'; // ✅ Green
      } else if (percentageUsed < 100) {
        return 'rgba(247, 144, 9, 1)'; // 🟠 Orange
      } else {
        return 'rgba(240, 68, 56, 1)'; // 🔴 Red
      }
    };

    if (isMapRank) {
      return getColorByUsage(mapRankUsed, mapRankTotal);
    } else if (isKeywordTracking) {
      return getColorByUsage(keywordUsed, keywordLimit);
    } else {
      return getColorByUsage(finalizedUsedLimit, finalizedAddonUsedLimit);
    }

  };

  const handleClose = () => {
    setOpen(null);
  };

  const getProgressBgColor = (progress = 0) => {
    const getBgByUsage = (used: number, total: number) => {
      if (total === 0) return 'var(--color-success-bg)'; // light green default

      const percentageUsed = (used / total) * 100;

      if (percentageUsed < 60) {
        return 'var(--color-success-bg)'; // ✅ Light green
      } else if (percentageUsed < 100) {
        return 'rgba(255, 238, 210, 1)'; // 🟠 Light orange
      } else {
        return 'rgba(253, 232, 232, 1)'; // 🔴 Light red
      }
    };

    if (isMapRank) {
      return getBgByUsage(mapRankUsed, mapRankTotal);
    } else if (isKeywordTracking) {
      return getBgByUsage(keywordUsed, keywordLimit);
    } else {
      return getBgByUsage(finalizedUsedLimit, finalizedAddonUsedLimit);
    }
  };

  return (
    <Fragment>
      <Snackbar
        sx={{ marginTop: '2.8rem' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
      >
        <Box>
          {!isError && (
            <Alert
              icon={<img src="/static/images/icons/circleGreenCheckIcon.svg" />}
              onClose={handleClose}
              severity="success"
              variant="filled"
              sx={{ ...snackBar?.alert, ...searchProgressSx?.alertSx }}
            >
              {selectedSearches}
              {searchesProgressBarTexts?.snackBarDesc}
            </Alert>
          )}
          {isError && (
            <Alert
              onClose={handleClose}
              severity="error"
              variant="filled"
              sx={{ ...snackBar?.alertError }}
            >
              {isError}
            </Alert>
          )}
        </Box>
      </Snackbar>
      <Box className="flex justify-between mt-1 ">
        <Box sx={searchProgressSx?.headingBox}>
          {finalizedUsedLimit >= finalizedAddonUsedLimit &&
            !isKeywordTracking &&
            !isMapRank && (
              <img
                src={
                  process.env.PUBLIC_URL +
                  `/static/images/icons/keyword/info-circle.svg`
                }
                style={{ marginRight: '3px' }}
                alt="icon"
              />
            )}
          {keywordUsed >= keywordLimit && isKeywordTracking && (
            <img
              src={
                process.env.PUBLIC_URL +
                `/static/images/icons/keyword/info-circle.svg`
              }
              style={{ marginRight: '3px' }}
              alt="icon"
            />
          )}
          {mapRankUsed >= mapRankTotal && isMapRank && (
            <img
              src={
                process.env.PUBLIC_URL +
                `/static/images/icons/keyword/info-circle.svg`
              }
              style={{ marginRight: '3px' }}
              alt="icon"
            />
          )}
          <Typography className="font-inter" sx={searchProgressSx?.text}>
            <b style={searchProgressSx?.boldTag}>
              {/* {isPremium */}
              {isMapRank
                ? mapRankUsed
                : isKeywordTracking
                ? keywordUsed
                : finalizedUsedLimit || 0}
              {/* : subscription.used_scan || 20} */}
            </b>{' '}
            of{' '}
            <b style={searchProgressSx?.boldTag}>
              {/* {isPremium ?  */}
              {isMapRank
                ? mapRankTotal
                : isKeywordTracking
                ? keywordLimit
                : finalizedAddonUsedLimit || 0}

              {/* : subscription.scan || 100} */}
            </b>{' '}
            {isKeywordTracking ? availableKeywordsUse : availableSearchUse}
          </Typography>
          {!isMapRank && (
            <CustomTooltip
              title={
                <Box>
                  {totalAddonLimit && !isKeywordTracking ? (
                    <Typography
                      sx={{
                        ...keywordResearchDashBoardCss?.customTollTipText,
                        lineHeight: 'var(--line-height-22)'
                      }}
                    >
                      <span
                        style={
                          keywordResearchDashBoardCss?.customTollTipTextDark
                        }
                      >
                        {searchesDetailRedux?.['usedLimit']}
                      </span>
                      {` of `}
                      <span
                        style={
                          keywordResearchDashBoardCss?.customTollTipTextDark
                        }
                      >
                        {searchesDetailRedux?.['totalLimit']}
                      </span>
                      {` search credits used `}
                      <br />
                      <span
                        style={
                          keywordResearchDashBoardCss?.customTollTipTextDark
                        }
                      >
                        {totalAddonUsedLimit}
                      </span>
                      {` of `}
                      <span
                        style={
                          keywordResearchDashBoardCss?.customTollTipTextDark
                        }
                      >
                        {totalAddonLimit}
                      </span>
                      {` extra search credits used`}
                    </Typography>
                  ) : (
                    <Typography
                      sx={keywordResearchDashBoardCss?.customTollTipText}
                    >
                      <span
                        style={
                          keywordResearchDashBoardCss?.customTollTipTextDark
                        }
                      >
                        {isKeywordTracking
                          ? ``
                          : searchesProgressBarTexts?.toolTipHeader}
                      </span>
                      {isKeywordTracking
                        ? searchesProgressBarTexts?.addKeywordToolTipDescription
                        : searchesProgressBarTexts?.toolTipDescription}
                    </Typography>
                  )}
                </Box>
              }
              placement="bottom"
              arrow
            >
              <HelpIcon sx={searchProgressSx?.helpIcon} />
            </CustomTooltip>
          )}
        </Box>
        {more &&
          !isEnterprise &&
          userDetails?.role_type !== userRoles.member &&
          !isKeywordTracking && (
            <Link
              component="button"
              onClick={() => {
                if (isDIY) {
                  dispatch(setIsOpenChangePlan(true));
                  navigate(paths?.billing);
                } else {
                  setOpenCreditModal(true);
                }
              }}
              style={{
                position: 'relative',
                fontWeight: 700,
                fontSize: 'var(--font-size-14)',
                lineHeight: 'var(--line-height-20)',
                textAlign: 'right',
                letterSpacing: '0.2px',
                color: 'var(--color-success-dark)',
                textDecoration: 'underline',
                textUnderlineOffset: "2px"
              }}
            >
              {GetMoreSearches}
            </Link>
          )}
      </Box>
      <Box className="flex mt-1">
        <LinearProgress
          variant="determinate"
          value={scanDetails}
          // value={20}
          className="w-full"
          //   color={getSearchProgressColor(scanDetails)}
          sx={{
            ...searchProgressSx?.linearProgress,
            bgcolor: getProgressBgColor(scanDetails), // dynamic background
            '& .MuiLinearProgress-bar': {
              backgroundColor: getSearchProgressColor(scanDetails)
            }
          }}
        />
      </Box>
      <CommonModal
        open={openCreditModal}
        handCloseModal={() => setOpenCreditModal(true)}
        title={''}
        closebtn={false}
        blur={false}
        isBuySearchCredit={true}
      >
        <BuySearchCreditModal
          handleCloseCreditModal={handleCloseCreditModal}
          handleCloseProcessCreditModal={handleCloseProcessCreditModal}
          setOpenCreditProcessModal={setOpenCreditProcessModal}
          setIsError={setIsError}
        />
      </CommonModal>
      <CommonModal
        open={openCreditProcessModal}
        handCloseModal={setOpenCreditProcessModal}
        title={''}
        closebtn={false}
        blur={false}
        isBuySearchCredit={false}
      >
        <GenerateIdeasLoader
          title={'Adding search credits...'}
        ></GenerateIdeasLoader>
      </CommonModal>
    </Fragment>
  );
};

export default SearchProgress;
